/**
 * Czysta logika układu domu (WP7) — bez Phaser.
 * Walidacja granic / occupancy zbieżna z @pieskowo/shared validateLayout.
 */
import {
  BLOCKS,
  GRID_COLS,
  GRID_ROWS,
  MAX_BLOCKS,
  type BlockDef,
  type PlacedBlock,
  validateLayout,
} from '@pieskowo/shared';

export const LOCAL_HOME_LAYOUT_KEY = 'pieskowo.homeLayout';
export const LOCAL_OWNED_BLOCKS_KEY = 'pieskowo.ownedBlocks';
export const LOCAL_HOME_VERSION_KEY = 'pieskowo.homeLayoutVersion';

/** Bloki domowe (bez stajni) — katalog mebli/ścian. */
export function homeBlockCatalog(): BlockDef[] {
  return BLOCKS.filter((b) => b.category !== 'stable');
}

/** Startowy zapas offline (gdy brak tokena / owned z serwera). */
export function defaultOwnedHomeBlocks(qtyPerId = 8): string[] {
  const out: string[] = [];
  for (const b of homeBlockCatalog()) {
    for (let i = 0; i < qtyPerId; i++) out.push(b.id);
  }
  return out;
}

export function normalizeLayout(raw: unknown): PlacedBlock[] {
  if (!Array.isArray(raw)) return [];
  const out: PlacedBlock[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    if (typeof o.id !== 'string') continue;
    if (!Number.isInteger(o.x) || !Number.isInteger(o.y) || !Number.isInteger(o.layer)) {
      continue;
    }
    const x = o.x as number;
    const y = o.y as number;
    const layer = o.layer as number;
    if (x < 0 || x >= GRID_COLS || y < 0 || y >= GRID_ROWS) continue;
    out.push({ id: o.id, x, y, layer });
  }
  return out;
}

export function occupancyKey(x: number, y: number, layer: number): string {
  return `${x},${y},${layer}`;
}

export function findAt(
  layout: readonly PlacedBlock[],
  x: number,
  y: number,
  layer?: number,
): PlacedBlock | undefined {
  if (layer === undefined) {
    // najwyższa warstwa na kaflu
    const at = layout.filter((b) => b.x === x && b.y === y);
    if (at.length === 0) return undefined;
    return at.reduce((a, b) => (b.layer >= a.layer ? b : a));
  }
  return layout.find((b) => b.x === x && b.y === y && b.layer === layer);
}

export function countUsed(layout: readonly PlacedBlock[], blockId: string): number {
  return layout.reduce((n, b) => n + (b.id === blockId ? 1 : 0), 0);
}

export function countOwned(owned: readonly string[], blockId: string): number {
  return owned.reduce((n, id) => n + (id === blockId ? 1 : 0), 0);
}

export function remainingOwned(
  owned: readonly string[],
  layout: readonly PlacedBlock[],
  blockId: string,
): number {
  return Math.max(0, countOwned(owned, blockId) - countUsed(layout, blockId));
}

export type PlaceResult =
  | { ok: true; layout: PlacedBlock[] }
  | { ok: false; reason: string };

/**
 * Postaw blok na siatce (domyślnie layer 0; meble na podłodze mogą iść na layer 1).
 */
export function placeBlock(
  layout: readonly PlacedBlock[],
  block: PlacedBlock,
  owned: readonly string[],
): PlaceResult {
  if (layout.length >= MAX_BLOCKS) return { ok: false, reason: 'too_many' };
  if (block.x < 0 || block.x >= GRID_COLS || block.y < 0 || block.y >= GRID_ROWS) {
    return { ok: false, reason: 'oob' };
  }
  if (findAt(layout, block.x, block.y, block.layer)) {
    return { ok: false, reason: 'overlap' };
  }
  if (remainingOwned(owned, layout, block.id) < 1) {
    return { ok: false, reason: 'qty' };
  }
  const next = [...layout, { ...block }];
  const v = validateLayout(next, owned as string[]);
  if (!v.ok) return { ok: false, reason: v.reason };
  return { ok: true, layout: next };
}

/** Chowanie do pudełka — usunięcie z siatki (blok wraca do palety). */
export function removeBlockAt(
  layout: readonly PlacedBlock[],
  x: number,
  y: number,
  layer?: number,
): PlacedBlock[] {
  if (layer === undefined) {
    const top = findAt(layout, x, y);
    if (!top) return [...layout];
    return layout.filter(
      (b) => !(b.x === x && b.y === y && b.layer === top.layer),
    );
  }
  return layout.filter((b) => !(b.x === x && b.y === y && b.layer === layer));
}

/** Sugerowana warstwa: podłoga/ściana/dach = 0, reszta = 1. */
export function defaultLayerForBlock(blockId: string): number {
  const def = BLOCKS.find((b) => b.id === blockId);
  if (!def) return 1;
  if (def.category === 'floor' || def.category === 'wall' || def.category === 'roof') {
    return 0;
  }
  return 1;
}

/** Pozycja konsoli: obok TV, albo stały kącik salonu. */
export function consoleShelfTile(layout: readonly PlacedBlock[]): { x: number; y: number } {
  const tv = layout.find((b) => b.id === 'tv_retro');
  if (tv) {
    const nx = Math.min(GRID_COLS - 1, tv.x + 1);
    return { x: nx, y: tv.y };
  }
  return { x: 14, y: 6 };
}
